import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material';
import { GatewayService } from 'src/app/gateway.service';
import { LocalSaveService } from 'src/app/services/local-save-service.service';
import { TipoServicioService } from '../tipo-servicio.service';
import { DescargarLotesComponent } from '../descargar-lotes/descargar-lotes.component';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-monitoreo',
  templateUrl: './monitoreo.component.html',
  styleUrls: ['./monitoreo.component.scss']
})
export class MonitoreoComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  private tipoServicioSub: Subscription;

  // --- Filtros ---
  fechaDesde: Date = new Date();
  fechaHasta: Date = new Date();
  minFechaDesde: Date = new Date(2026, 2, 1);
  filtroContrata: any[] = [];
  filtroAmPm: string = '';   // '' | 'AM' | 'PM'
  listaContratas: any[] = [];

  // --- KPIs ---
  kpis: any = {
    total_ordenes: 0,
    avg_por_tecnico: 0,
    pct_efectividad: 0,
    pct_cumplimiento: 0,
    ordenes_riesgo: 0,
    fallidas: 0
  };

  // --- Tabla principal ---
  dataPrincipal: any[] = [];
  totalRow: any = {};
  colsPrincipal: string[] = [
    'PROVINCIA',
    'CITAS', 'SIN_TECNICO',
    'ATENDIDAS', 'EN_SITIO', 'FALLIDAS',
    'PCT_EFECTIVIDAD', 'PCT_CUMPLIMIENTO',
    'EN_RIESGO', 'SIN_ATENDER'
  ];

  // --- Heatmaps ---
  dataAtendidasMap: any = {};
  dataInfructuosoMap: any = {};
  dataSubtipologiaMap: any = {};
  subtipologiasNombres: string[] = [];
  dataSubtipoCumplidasMap: any = {};
  subtipoCumplidasNombres: string[] = [];
  dataSubtipoIncumplidasMap: any = {};
  subtipoIncumplidasNombres: string[] = [];

  filtroAmPmAtendidas: string = '';
  filtroAmPmInfructuoso: string = '';

  horasColumnas: number[] = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
  diasSemana: string[] = ['Lunes', 'Martes', 'Mi\u00e9rcoles', 'Jueves', 'Viernes', 'S\u00e1bado', 'Domingo'];

  // Mapeo de day names en ingl\u00e9s a espa\u00f1ol
  private dayMap: any = {
    'Monday': 'Lunes', 'Tuesday': 'Martes', 'Wednesday': 'Mi\u00e9rcoles',
    'Thursday': 'Jueves', 'Friday': 'Viernes', 'Saturday': 'S\u00e1bado', 'Sunday': 'Domingo'
  };

  // --- Loading states ---
  loadingDash = false;
  loadingAtendidas = false;
  loadingInfructuoso = false;
  loadingSubtipo = false;

  // --- Alarmas (conservar funcionalidad existente) ---
  loading1 = false;
  loading3 = false;
  cols1 = ['provincia', 'tecnico', 'criticas', 'cantalarmas'];
  cols3 = ['provincia', 'tipo', 'descripcion', 'orden', 'tecnico', 'fechahora'];
  datareporte1: any[] = [];
  datareporte3: any[] = [];
  alarmas_detalle: any[] = [];
  selectedRow: any = null;

  // --- Visibilidad de RGUs (solo Instalaciones IDAREA=14) ---
  esInstalaciones: boolean = true;

  constructor(
    private gateway: GatewayService,
    public localSave: LocalSaveService,
    private _tipoServicio: TipoServicioService,
    private dialog: MatDialog,
  ) { }

  ngOnInit() {
    this.tipoServicioSub = this._tipoServicio.notifyObservable$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.esInstalaciones = val.IDAREA === 14;
      this.filtroContrata = [];
      this.getContratasAgente();
      this.cargarTodo();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.tipoServicioSub) {
      this.tipoServicioSub.unsubscribe();
    }
  }

  cargarTodo() {
    this.cargarDashboard();
    this.cargarDetalleAtendidas();
    this.cargarDetalleInfructuoso();
    this.cargarSubtipologia();
    this.reporte1();
    this.reporte3();
  }

  onFiltroChange() {
    this.cargarDashboard();
    this.cargarDetalleAtendidas();
    this.cargarDetalleInfructuoso();
    this.cargarSubtipologia();
  }

  onFiltroAmPmAtendidasChange() {
    this.cargarDetalleAtendidas();
  }

  onFiltroAmPmInfructuosoChange() {
    this.cargarDetalleInfructuoso();
    this.cargarSubtipologia();
  }

  onContrataChange() {
    this.onFiltroChange();
  }

  getContratasAgente() {
    this.gateway.get('getContrata', {
      NUM_PAGINA: 0,
      FILTRO: null,
      NUM_ELEMENTOS: 5,
      IDPAIS: this.localSave.get('pais'),
      IDAREA: (this._tipoServicio.getValorActual().IDAREA || null)
    }).then((result: any) => {
      this.listaContratas = result.rows || [];
    }).catch((e) => {
      this.listaContratas = [];
    });
  }

  private getContrataValue(): any {
    const user = this.localSave.get('usuarioPGV');
    if (user && user.IDCONTRATA2) {
      return user.IDCONTRATA2;
    }
    return (this.filtroContrata && this.filtroContrata.length > 0) ? this.filtroContrata : null;
  }

  // =============================================
  // DASHBOARD PRINCIPAL (KPIs + Tabla por provincia)
  // =============================================
  cargarDashboard() {
    this.loadingDash = true;
    const params = {
      fechaDesde: this.formatDate(this.fechaDesde),
      fechaHasta: this.formatDate(this.fechaHasta),
      iddistribuidor: (this._tipoServicio.getValorActual().IDU || null),
      idarea: (this._tipoServicio.getValorActual().IDAREA || null),
      idcontrata: this.getContrataValue(),
      am_pm: this.filtroAmPm || null
    };

    this.gateway.get('getReporteDash', params).then((result: any) => {
      this.kpis = result.kpis || {};
      this.dataPrincipal = result.rows || [];
      this.calcularTotalRow();
      this.loadingDash = false;
    }).catch((e) => {
      this.kpis = {};
      this.dataPrincipal = [];
      this.totalRow = {};
      this.loadingDash = false;
    });
  }

  calcularTotalRow() {
    if (!this.dataPrincipal.length) {
      this.totalRow = {};
      return;
    }
    const t: any = {
      PROVINCIA: 'TOTAL',
      CITAS: 0, SIN_TECNICO: 0,
      ATENDIDAS: 0, EN_SITIO: 0, FALLIDAS: 0,
      EN_RIESGO: 0, SIN_ATENDER: 0
    };
    for (const row of this.dataPrincipal) {
      t.CITAS += (row.CITAS || 0);
      t.SIN_TECNICO += (row.SIN_TECNICO || 0);
      t.ATENDIDAS += (row.ATENDIDAS || 0);
      t.EN_SITIO += (row.EN_SITIO || 0);
      t.FALLIDAS += (row.FALLIDAS || 0);
      t.EN_RIESGO += (row.EN_RIESGO || 0);
      t.SIN_ATENDER += (row.SIN_ATENDER || 0);
    }
    t.PCT_EFECTIVIDAD = t.CITAS > 0 ? (t.ATENDIDAS / t.CITAS * 100) : 0;
    t.PCT_CUMPLIMIENTO = this.kpis.pct_cumplimiento || 0;
    this.totalRow = t;
  }

  // =============================================
  // DETALLE ATENDIDAS (heatmap hora x d\u00eda)
  // =============================================
  cargarDetalleAtendidas() {
    this.loadingAtendidas = true;
    const params = {
      fechaDesde: this.formatDate(this.fechaDesde),
      fechaHasta: this.formatDate(this.fechaHasta),
      iddistribuidor: (this._tipoServicio.getValorActual().IDU || null),
      idarea: (this._tipoServicio.getValorActual().IDAREA || null),
      idcontrata: this.getContrataValue(),
      am_pm: this.filtroAmPmAtendidas || null,
      tipo: 'ATENDIDAS'
    };

    this.gateway.get('getDetalleHora', params).then((result: any) => {
      this.dataAtendidasMap = this.buildHeatmapMap(result.rows || []);
      this.loadingAtendidas = false;
    }).catch((e) => {
      this.dataAtendidasMap = {};
      this.loadingAtendidas = false;
    });
  }

  // =============================================
  // DETALLE INFRUCTUOSO (heatmap hora x d\u00eda)
  // =============================================
  cargarDetalleInfructuoso() {
    this.loadingInfructuoso = true;
    const params = {
      fechaDesde: this.formatDate(this.fechaDesde),
      fechaHasta: this.formatDate(this.fechaHasta),
      iddistribuidor: (this._tipoServicio.getValorActual().IDU || null),
      idarea: (this._tipoServicio.getValorActual().IDAREA || null),
      idcontrata: this.getContrataValue(),
      am_pm: this.filtroAmPmInfructuoso || null,
      tipo: 'INFRUCTUOSO'
    };

    this.gateway.get('getDetalleHora', params).then((result: any) => {
      this.dataInfructuosoMap = this.buildHeatmapMap(result.rows || []);
      this.loadingInfructuoso = false;
    }).catch((e) => {
      this.dataInfructuosoMap = {};
      this.loadingInfructuoso = false;
    });
  }

  // =============================================
  // DETALLE SUBTIPOLOG\u00cdAS
  // =============================================
  cargarSubtipologia() {
    this.loadingSubtipo = true;
    const params = {
      fechaDesde: this.formatDate(this.fechaDesde),
      fechaHasta: this.formatDate(this.fechaHasta),
      iddistribuidor: (this._tipoServicio.getValorActual().IDU || null),
      idarea: (this._tipoServicio.getValorActual().IDAREA || null),
      idcontrata: this.getContrataValue(),
      am_pm: this.filtroAmPmInfructuoso || null
    };

    this.gateway.get('getDetalleSubtipologia', params).then((result: any) => {
      // Procesar TOTAL
      const totalRows = result.total || [];
      this.dataSubtipologiaMap = {};
      const nombresSetTotal = new Set<string>();
      for (const r of totalRows) {
        const nombre = r.subtipologia || 'Sin nombre';
        nombresSetTotal.add(nombre);
        const key = `${nombre}_${r.hora}`;
        this.dataSubtipologiaMap[key] = (this.dataSubtipologiaMap[key] || 0) + r.cantidad;
      }
      this.subtipologiasNombres = Array.from(nombresSetTotal).sort((a, b) => a.localeCompare(b));

      // Procesar CUMPLIDAS
      const cumplidasRows = result.cumplidas || [];
      this.dataSubtipoCumplidasMap = {};
      const nombresSetCumpl = new Set<string>();
      for (const r of cumplidasRows) {
        const nombre = r.subtipologia || 'Sin nombre';
        nombresSetCumpl.add(nombre);
        const key = `${nombre}_${r.hora}`;
        this.dataSubtipoCumplidasMap[key] = (this.dataSubtipoCumplidasMap[key] || 0) + r.cantidad;
      }
      this.subtipoCumplidasNombres = Array.from(nombresSetCumpl).sort((a, b) => a.localeCompare(b));

      // Procesar INCUMPLIDAS
      const incumplidasRows = result.incumplidas || [];
      this.dataSubtipoIncumplidasMap = {};
      const nombresSetIncumpl = new Set<string>();
      for (const r of incumplidasRows) {
        const nombre = r.subtipologia || 'Sin nombre';
        nombresSetIncumpl.add(nombre);
        const key = `${nombre}_${r.hora}`;
        this.dataSubtipoIncumplidasMap[key] = (this.dataSubtipoIncumplidasMap[key] || 0) + r.cantidad;
      }
      this.subtipoIncumplidasNombres = Array.from(nombresSetIncumpl).sort((a, b) => a.localeCompare(b));

      this.loadingSubtipo = false;
    }).catch((e) => {
      this.dataSubtipologiaMap = {};
      this.subtipologiasNombres = [];
      this.dataSubtipoCumplidasMap = {};
      this.subtipoCumplidasNombres = [];
      this.dataSubtipoIncumplidasMap = {};
      this.subtipoIncumplidasNombres = [];
      this.loadingSubtipo = false;
    });
  }

  // =============================================
  // ALARMAS (funcionalidad existente conservada)
  // =============================================
  reporte1() {
    this.loading1 = true;
    const fecha = new Date();
    const filtrofecha = `${fecha.getFullYear()}-${this.dosDig(fecha.getMonth() + 1)}-${this.dosDig(fecha.getDate())}`;

    this.gateway.get('getreporte1', { filtrofecha, iddistribuidor: (this._tipoServicio.getValorActual().IDU || null) }).then((result) => {
      const aux: any = result;
      this.datareporte1 = aux.rows;
      for (const row of this.datareporte1) {
        row.CANTALARMAS = 0;
      }
      this.contarAlarmas();
      this.loading1 = false;
    }).catch((e) => {
      this.loading1 = false;
    });
  }

  reporte3(row: any = null) {
    this.loading3 = true;
    this.gateway.get('getalarmasxarea', {
      estados: [],
      fecha: null,
      am_pm: null,
      idcontrata: null,
      provincias: [],
      tecnicos: [],
      iddistribuidor: (this._tipoServicio.getValorActual().IDU || null),
      idarea: (this._tipoServicio.getValorActual().IDAREA || null),
      IDCONTRATA2: this.localSave.get('usuarioPGV').IDCONTRATA2,
      prioridad: null,
    }).then((result) => {
      const aux: any = result;
      this.alarmas_detalle = aux.alaramas_detalle;
      this.alarmas_detalle.sort((a, b) => Number(a.id_provincia) - Number(b.id_provincia));
      let alarmas_filtradas = [];
      if (row != null) {
        alarmas_filtradas = this.alarmas_detalle.filter(a => a.id_provincia == row.idprovincia);
      } else {
        alarmas_filtradas = this.alarmas_detalle;
      }
      this.datareporte3 = alarmas_filtradas;
      this.loading3 = false;
      this.contarAlarmas();
    }).catch((e) => {
      this.loading3 = false;
    });
  }

  onRowClick(row: any) {
    if (this.selectedRow === row) {
      this.selectedRow = null;
      this.reporte3(null);
    } else {
      this.selectedRow = row;
      this.reporte3(row);
    }
  }

  contarAlarmas() {
    if (this.datareporte1.length && this.alarmas_detalle.length) {
      for (const row of this.datareporte1) {
        row.CANTALARMAS = this.alarmas_detalle.filter(a => a.id_provincia == row.idprovincia).length;
      }
    }
  }

  // =============================================
  // UTILIDADES
  // =============================================
  buildHeatmapMap(rows: any[]): any {
    const map: any = {};
    for (const r of rows) {
      const diaEs = this.dayMap[r.dia_semana] || r.dia_semana;
      const key = `${diaEs}_${r.hora}`;
      map[key] = (map[key] || 0) + r.cantidad;
    }
    return map;
  }

  getHeatmapValue(mapData: any, dia: string, hora: number): number {
    return mapData[`${dia}_${hora}`] || 0;
  }

  getHeatmapRowTotal(mapData: any, dia: string): number {
    let total = 0;
    for (const h of this.horasColumnas) {
      total += this.getHeatmapValue(mapData, dia, h);
    }
    return total;
  }

  getHeatmapColTotal(mapData: any, hora: number): number {
    let total = 0;
    for (const d of this.diasSemana) {
      total += this.getHeatmapValue(mapData, d, hora);
    }
    return total;
  }

  getHeatmapGrandTotal(mapData: any): number {
    let total = 0;
    for (const d of this.diasSemana) {
      for (const h of this.horasColumnas) {
        total += this.getHeatmapValue(mapData, d, h);
      }
    }
    return total;
  }

  getSubtipologiaValue(nombre: string, hora: number): number {
    return this.dataSubtipologiaMap[`${nombre}_${hora}`] || 0;
  }

  getSubtipologiaRowTotal(nombre: string): number {
    let total = 0;
    for (const h of this.horasColumnas) {
      total += this.getSubtipologiaValue(nombre, h);
    }
    return total;
  }

  getSubtipologiaColTotal(hora: number): number {
    let total = 0;
    for (const n of this.subtipologiasNombres) {
      total += this.getSubtipologiaValue(n, hora);
    }
    return total;
  }

  getSubtipologiaGrandTotal(): number {
    let total = 0;
    for (const n of this.subtipologiasNombres) {
      for (const h of this.horasColumnas) {
        total += this.getSubtipologiaValue(n, h);
      }
    }
    return total;
  }

  // --- Helpers genéricos para subtipologías (cumplidas/incumplidas) ---
  getSubtipoGenValue(map: any, nombre: string, hora: number): number {
    return map[`${nombre}_${hora}`] || 0;
  }

  getSubtipoGenRowTotal(map: any, nombre: string): number {
    let total = 0;
    for (const h of this.horasColumnas) {
      total += this.getSubtipoGenValue(map, nombre, h);
    }
    return total;
  }

  getSubtipoGenColTotal(map: any, nombres: string[], hora: number): number {
    let total = 0;
    for (const n of nombres) {
      total += this.getSubtipoGenValue(map, n, hora);
    }
    return total;
  }

  getSubtipoGenGrandTotal(map: any, nombres: string[]): number {
    let total = 0;
    for (const n of nombres) {
      for (const h of this.horasColumnas) {
        total += this.getSubtipoGenValue(map, n, h);
      }
    }
    return total;
  }

  getColorIndicator(pct: number): string {
    if (pct >= 80) return 'indicator-green';
    if (pct >= 60) return 'indicator-yellow';
    return 'indicator-red';
  }

  getHeatmapCellClass(value: number, mapData: any): string {
    if (!value || value === 0) return '';
    // Buscar max para determinar intensidad relativa
    let max = 0;
    for (const key in mapData) {
      if (mapData[key] > max) max = mapData[key];
    }
    if (max === 0) return '';
    const ratio = value / max;
    if (ratio >= 0.75) return 'heat-high';
    if (ratio >= 0.4) return 'heat-medium';
    return 'heat-low';
  }

  formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // =============================================
  // DESCARGA EXCEL
  // =============================================
  descargar() {
    const ahora = new Date();
    const ts = ahora.getFullYear()
      + ('0' + (ahora.getMonth() + 1)).slice(-2)
      + ('0' + ahora.getDate()).slice(-2)
      + '_'
      + ('0' + ahora.getHours()).slice(-2)
      + ('0' + ahora.getMinutes()).slice(-2);

    const params = {
      fechaDesde: this.formatDate(this.fechaDesde),
      fechaHasta: this.formatDate(this.fechaHasta),
      iddistribuidor: (this._tipoServicio.getValorActual().IDU || null),
      idarea: (this._tipoServicio.getValorActual().IDAREA || null),
      idcontrata: this.getContrataValue(),
      am_pm: this.filtroAmPm || null
    };

    this.dialog.open(DescargarLotesComponent, {
      disableClose: true,
      width: '700px',
      data: {
        cantPorPagina: 1000,
        nombreArchivo: `reporte_monitoreo_${ts}.xlsx`,
        datasets: [
          {
            nombreServicioCantRegistros: 'getMonitoreoDescargaDedupCount',
            nombreServicioRegistros: 'getMonitoreoDescargaDedupData',
            nombrePestana: 'Efectividad y Cumplimiento',
            params: params
          },
          {
            nombreServicioCantRegistros: 'getMonitoreoDescargaCount',
            nombreServicioRegistros: 'getMonitoreoDescargaData',
            nombrePestana: 'Detalle de Citas',
            params: params
          }
        ]
      }
    });
  }

  dosDig(num: number): string {
    return num < 10 ? '0' + num : '' + num;
  }
}
